import React, { useState, useEffect } from 'react';

/**
 * DSStepper - Design System Stepper Component
 *
 * Renders a multi-step progress indicator aligned with the InferMed design system.
 * Adapts between horizontal and vertical orientation based on screen size.
 *
 * Props:
 *  - activeStep: number (current active step index)
 *  - steps: string[] (array of step labels)
 *  - orientation: 'horizontal' | 'vertical' (default: 'horizontal')
 */

const SR_ONLY = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  padding: 0,
  margin: '-1px',
  overflow: 'hidden',
  clip: 'rect(0,0,0,0)',
  whiteSpace: 'nowrap',
  border: 0,
};

export function DSStepper({ activeStep, steps, orientation = 'horizontal', glass = false }) {
  const [isNarrow, setIsNarrow] = useState(
    typeof window !== 'undefined' && window.innerWidth < 480
  );

  useEffect(() => {
    const handleResize = () => setIsNarrow(window.innerWidth < 480);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <nav aria-label="Form steps">
      {/* Announces current position to screen readers immediately */}
      <span style={SR_ONLY}>Step {activeStep + 1} of {steps.length}</span>
      <div
        className="stepper-container"
        data-orientation={orientation}
        role="list"
        style={{
          display: 'flex',
          flexDirection: orientation === 'vertical' ? 'column' : 'row',
          gap: orientation === 'vertical' ? 'var(--space-4)' : 'var(--space-2)',
          marginBottom: orientation === 'vertical' ? 'var(--space-4)' : 'var(--space-6)',
          flexWrap: 'wrap',
          alignItems: orientation === 'vertical' ? 'flex-start' : 'center',
          ...(glass && {
            backgroundColor: 'var(--glass-stepper-bg)',
            backdropFilter: 'blur(var(--glass-tab-blur))',
            WebkitBackdropFilter: 'blur(var(--glass-tab-blur))',
            borderRadius: 'var(--radius-lg)',
            border: '1px solid var(--glass-tab-border)',
            padding: 'var(--space-3) var(--space-4)',
            boxShadow: 'var(--glass-shadow-sm)',
          }),
        }}
      >
        {steps.map((label, index) => {
          const isActive = index === activeStep;
          const isCompleted = index < activeStep;

          return (
            <React.Fragment key={index}>
              {/* Step */}
              <div
                className={`stepper-step ${isActive ? 'active' : ''} ${isCompleted ? 'completed' : ''}`}
                role="listitem"
                aria-current={isActive ? 'step' : undefined}
                style={{
                  display: 'flex',
                  alignItems: orientation === 'vertical' ? 'flex-start' : 'center',
                  gap: 'var(--space-2)',
                  flex: orientation === 'horizontal' ? '1 1 auto' : 'none',
                  minWidth: orientation === 'horizontal' ? 'auto' : '100%',
                  position: 'relative',
                  minHeight: '40px',
                }}
              >
                {/* Visually-hidden full description for screen readers */}
                <span style={SR_ONLY}>
                  {isCompleted
                    ? `Step ${index + 1}: ${label}, completed`
                    : isActive
                    ? `Step ${index + 1}: ${label}, current step`
                    : `Step ${index + 1}: ${label}`}
                </span>

                {/* Step Circle — decorative, aria-hidden */}
                <div
                  aria-hidden="true"
                  className="stepper-step-circle"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 'clamp(32px, 10vw, 40px)',
                    height: 'clamp(32px, 10vw, 40px)',
                    minWidth: 'clamp(32px, 10vw, 40px)',
                    minHeight: 'clamp(32px, 10vw, 40px)',
                    borderRadius: '50%',
                    fontWeight: 700,
                    fontSize: 'clamp(0.75rem, 2vw, 0.9rem)',
                    transition: 'all var(--duration-fast) var(--ease-out)',
                    backgroundColor:
                      glass && isActive
                        ? 'rgba(93, 175, 150, 0.18)'
                        : (isActive ? 'var(--teal-600)' : isCompleted ? 'var(--lime-600)' : 'var(--neutral-200)'),
                    color: isActive || isCompleted ? 'white' : 'var(--neutral-500)',
                    border: glass && isActive
                      ? '1px solid var(--glass-nav-border-active)'
                      : (isActive ? '2px solid var(--teal-600)' : 'none'),
                    ...(glass && isActive && {
                      backdropFilter: 'blur(4px)',
                      WebkitBackdropFilter: 'blur(4px)',
                    }),
                    flexShrink: 0,
                  }}
                >
                  {isCompleted ? '✓' : index + 1}
                </div>

                {/* Step Label */}
                <div
                  aria-hidden="true"
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 'var(--space-1)',
                    minWidth: 0,
                  }}
                >
                  <span
                    style={{
                      fontFamily: 'var(--font-display)',
                      fontWeight: isActive ? 700 : 600,
                      fontSize: orientation === 'horizontal' ? 'clamp(0.65rem, 2vw, 0.85rem)' : '0.85rem',
                      color: isActive ? 'var(--teal-600)' : isCompleted ? 'var(--lime-600)' : 'var(--neutral-500)',
                      transition: 'color var(--duration-fast) var(--ease-out)',
                      wordBreak: 'break-word',
                      overflowWrap: 'break-word',
                    }}
                  >
                    {label}
                  </span>
                  {orientation === 'vertical' && (
                    <span
                      style={{
                        fontSize: '0.7rem',
                        color: 'var(--neutral-400)',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                      }}
                    >
                      Step {index + 1}
                    </span>
                  )}
                </div>
              </div>

              {/* Connector Line — decorative, hidden from AT */}
              {index < steps.length - 1 && (
                <div
                  aria-hidden="true"
                  role="presentation"
                  className="stepper-connector"
                  style={{
                    flex: orientation === 'horizontal' ? '1 1 auto' : 'none',
                    width: orientation === 'horizontal' ? 'auto' : '2px',
                    minWidth: orientation === 'horizontal' ? 'var(--space-2)' : 'auto',
                    height: orientation === 'horizontal' ? '2px' : '16px',
                    backgroundColor: index < activeStep ? 'var(--lime-600)' : 'var(--neutral-200)',
                    margin: orientation === 'horizontal' ? '0 var(--space-1)' : `0 0 0 calc(clamp(16px, 5vw, 20px) / 2 - 1px)`,
                    transition: 'background-color var(--duration-fast) var(--ease-out)',
                    alignSelf: orientation === 'horizontal' ? 'center' : 'flex-start',
                    visibility: orientation === 'horizontal' && isNarrow ? 'hidden' : 'visible',
                  }}
                />
              )}
            </React.Fragment>
          );
        })}
      </div>
    </nav>
  );
}
